import {
  Component,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
} from '@angular/core'
import { FormControl, FormGroup } from '@angular/forms'
import { BlankSessionService } from '@helios/sessions'
import {
  NoteContentValidator,
  NotesValidator,
  notesValidationMessages,
} from '@helios/validators'
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap'
import { ToastService } from '@northfork/ng-basics'
import { Subject } from 'rxjs'
import { take, takeUntil } from 'rxjs/operators'
import {
  Break,
  BreakCodeResponse,
  BreakNoteHandling,
} from '../../model/break.model'
import { resolvePaste } from '../../model/break-code-paste'
import { keepWouldDropCode } from '../../model/bulk-break-group.model'
import { isPasteBreakCodesEvent } from '../../utils/paste-break-codes-event'
import { unescapeNoteNewlines } from '../../utils/note-formatter'
import { BreakCodeClipboardService } from '../../services/break-code-clipboard.service'
import { MultiBreaksService } from '../../services/multi-breaks.service'
import { BREAK_DIALOG_SHARED_STYLES } from '../break-dialog-shared.styles'
import { prefillSelection } from '../../validators/break-code-validation'
import { KeepMode } from '../../validators/bulk-break-validation'
import {
  EditNoteValidation,
  commonSelectionCodes,
  editNoteValidation,
} from '../../validators/edit-note-validation'

@Component({
  selector: 'hs-note-modal',
  templateUrl: './note-modal.component.html',
  styles: [
    BREAK_DIALOG_SHARED_STYLES,
    `
      nf-form-control {
        margin-bottom: 0;
      }
      .control-label {
        font-size: 11px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        color: #6b7a8d;
        margin-bottom: 2px;
      }
      .toggle-row {
        display: flex;
        gap: 40px;
        margin-bottom: 10px;
      }
      .segment-toggle {
        display: inline-flex;
        align-items: center;
        font-size: 12px;
      }
      .field-row {
        margin-bottom: 12px;
      }
      ::ng-deep .ng-select,
      ::ng-deep .ng-dropdown-panel .ng-option {
        font-size: 12px;
      }
      textarea.form-control {
        font-size: 12px;
      }
      .edit-note-codes-empty {
        font-size: 12px;
        font-style: italic;
        color: #6b7a8d;
        margin-bottom: 8px;
      }
      .save-disabled-reason {
        font-size: 12px;
        color: #db1f30;
        margin-right: auto;
      }
    `,
  ],
})
export class NoteModalComponent implements OnDestroy {
  private readonly destroy$ = new Subject<void>()

  private _breaks: Break[] = []

  issueIds: string[] = []
  modalTitle = 'Edit Note'
  saveBtnLabel = 'Save'

  // Global break codes view state. Untyped FormControls (Angular 12).
  private _breakCodes: BreakCodeResponse | undefined
  private userEdited = false

  // Cached so the template binds STABLE array references. ng-select breaks option
  // selection (items become unclickable) when [items] is a fresh array every
  // change-detection cycle — so these are recomputed only when breaks/breakCodes
  // change, NOT read from the editNoteVal getter. Mirrors BulkBreakModalComponent.
  actionItems: string[] = []
  descriptionItems: string[] = []
  notesRequiredActionCodeSet = new Set<string>()

  globalActionControl = new FormControl(null)
  globalDescriptionControl = new FormControl(null)
  globalNoteControl = new FormControl('', [
    NotesValidator(true),
    NoteContentValidator(),
  ])
  keepControl = new FormControl('keep')
  noteHandlingControl = new FormControl('override')

  readonly noteValidationMessages = notesValidationMessages

  // keepControl is intentionally NOT in this group — the toggle drives it via
  // standalone [ngModel] and keepMode reads it directly.
  editForm = new FormGroup({
    action: this.globalActionControl,
    description: this.globalDescriptionControl,
    note: this.globalNoteControl,
  })

  @Input() set breaks(values: Break[]) {
    if (values.length === 0) {
      this.activeModal.dismiss()
      return
    }

    this._breaks = values
    this.issueIds = values.map(b => b.identifier)
    if (values.length > 1) {
      this.modalTitle = `Edit Notes (${values.length})`
      this.saveBtnLabel = `Save (${values.length})`
    }
    this.recomputeCodeOptions()
    this.maybePrefill()
    this.recomputeNoteValidators()
  }
  get breaks(): Break[] {
    return this._breaks
  }

  @Input() set breakCodes(value: BreakCodeResponse | undefined) {
    this._breakCodes = value
    this.recomputeCodeOptions()
    this.maybePrefill()
    this.recomputeNoteValidators()
  }
  get breakCodes(): BreakCodeResponse | undefined {
    return this._breakCodes
  }

  get keepMode(): KeepMode {
    return this.keepControl.value as KeepMode
  }

  get resolvedNoteHandling(): BreakNoteHandling {
    return this.noteHandlingControl.value === 'append'
      ? BreakNoteHandling.APPEND
      : BreakNoteHandling.REPLACE
  }

  get editNoteVal(): EditNoteValidation {
    return editNoteValidation(
      this._breaks,
      this._breakCodes,
      {
        actionCode: this.globalActionControl.value ?? null,
        descriptionCode: this.globalDescriptionControl.value ?? null,
        note: this.globalNoteControl.value ?? '',
      },
      this.keepMode,
    )
  }

  /**
   * How many selected breaks will silently NOT receive the chosen codes — see
   * keepWouldDropCode for the per-field Keep semantics. Warns on the condition, not on
   * how the code got there (a hand-picked code behaves like a pasted one). Surfaced
   * rather than auto-switching to Override, which would rewrite EVERY selected break.
   */
  get keepBlockedCodeCount(): number {
    if (this.keepMode !== 'keep') return 0
    return this._breaks.filter(b =>
      keepWouldDropCode(
        b,
        this.globalActionControl.value ?? null,
        this.globalDescriptionControl.value ?? null,
      ),
    ).length
  }

  /** Whether the non-blocking save caution is showing under the toggle row. */
  get hasCautions(): boolean {
    return this.keepBlockedCodeCount > 0
  }

  formSubmitting = false
  // Intentionally empty: wires the outer <form>'s NfFormValidation/(validSubmit) so
  // Enter submits like any other form; Save's actual gating is editNoteVal.submitDisabled
  // on the button, and editForm (below) carries the real code/note controls.
  form = new FormGroup({})

  constructor(
    public activeModal: NgbActiveModal,
    public toastService: ToastService,
    private readonly breaksService: MultiBreaksService,
    private readonly blankSessionService: BlankSessionService,
    private readonly breakCodeClipboard: BreakCodeClipboardService,
    private readonly host: ElementRef<HTMLElement>,
  ) {
    // Any user-originated change to these controls stops re-prefilling so later
    // @Input updates can't clobber edits. Prefill itself sets values with
    // emitEvent:false, so these subscriptions only fire on real user input.
    this.globalActionControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.userEdited = true
        // A notes-required action code flips whether the note is required, so the
        // note control's allowEmpty must follow it.
        this.recomputeNoteValidators()
      })
    this.globalDescriptionControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.userEdited = true
      })
    this.globalNoteControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.userEdited = true
      })
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  async onSubmit() {
    // Mirrors BulkBreakModalComponent: never submit an invalid selection even if
    // onSubmit is reached outside the disabled button.
    if (this.editNoteVal.submitDisabled) return

    this.formSubmitting = true

    await this.blankSessionService.createBlankSessionIfNotInSession()

    const obs = this.breaksService.updateNote(
      this.issueIds,
      // An entered note overwrites (REPLACE); an empty note is downgraded to
      // APPEND (no-op) by the service. A whitespace-only note can't reach here
      // — it's an invalid note and Save is blocked above.
      this.globalNoteControl.value ?? '',
      this.resolvedNoteHandling,
      this.globalActionControl.value ?? undefined,
      this.globalDescriptionControl.value ?? undefined,
      this.keepMode === 'keep',
    )

    obs.pipe(take(1)).subscribe(
      response => {
        this.formSubmitting = false
        this.activeModal.close(response)
      },
      () => {
        this.formSubmitting = false
        this.toastService.error('Edit notes failed, request cancelled.')
        this.activeModal.dismiss(null)
      },
    )
  }

  get canPaste(): boolean {
    return this.breakCodeClipboard.hasContent
  }

  /**
   * Fill the code + note boxes from the in-app clipboard. setValue runs WITH events on
   * purpose: the valueChanges subscriptions set `userEdited`, which stops a later input
   * update from re-running maybePrefill() and overwriting what was just pasted —
   * including a box paste deliberately cleared.
   */
  onPasteBreakCodes(): void {
    const entry = this.breakCodeClipboard.read()
    if (!entry) {
      return
    }

    const result = resolvePaste(entry, this.actionItems, this.descriptionItems)
    this.globalActionControl.setValue(result.actionCode)
    this.globalDescriptionControl.setValue(result.descriptionCode)
    this.globalNoteControl.setValue(result.note)

    if (result.error) {
      this.toastService.error(result.error)
    }
  }

  /** alt+v — see isPasteBreakCodesEvent for why this is not a hotkeys-registry binding. */
  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (!isPasteBreakCodesEvent(event, this.host.nativeElement)) {
      return
    }
    event.preventDefault()
    this.onPasteBreakCodes()
  }

  /**
   * Prefill the view from the current breaks + codes: the shared note when every
   * break has the same one, and a single distinct existing code when still
   * configured. Runs on every @Input update until the user edits a control
   * (tracked via userEdited), so it survives breaks/breakCodes arriving in either
   * order with real values. setValue uses emitEvent:false so prefill doesn't trip
   * the userEdited guard.
   */
  private maybePrefill(): void {
    if (this.userEdited || !this._breakCodes || this._breaks.length === 0) {
      return
    }
    const action = prefillSelection(
      this._breaks.map(b => b.actionCode),
      this.actionItems,
    )
    const description = prefillSelection(
      this._breaks.map(b => b.descriptionCode),
      this.descriptionItems,
    )
    if (action) this.globalActionControl.setValue(action, { emitEvent: false })
    if (description)
      this.globalDescriptionControl.setValue(description, { emitEvent: false })
    const notes = this._breaks.map(b => unescapeNoteNewlines(b.note))
    const shared = notes.every(n => n === notes[0]) ? notes[0] : ''
    if (shared) this.globalNoteControl.setValue(shared, { emitEvent: false })
  }

  /**
   * Recompute the stable code-option arrays from the current breaks + codes. The
   * dropdown is the all-selection intersection (independent of keep/override and
   * of the chosen codes), so this only needs to run when breaks/breakCodes change.
   */
  private recomputeCodeOptions(): void {
    const common = commonSelectionCodes(this._breaks, this._breakCodes)
    this.actionItems = common.actionOptions
    this.descriptionItems = common.descriptionOptions
    this.notesRequiredActionCodeSet = common.notesRequiredActionCodeSet
  }

  /**
   * Recompute the note control's validators to mirror <hs-note-editor>'s rule:
   * allowEmpty follows whether a note is required (no codes available, or a
   * notes-required action code chosen), so an empty REQUIRED note red-borders while
   * an empty optional note does not. Re-run when breaks/breakCodes or the chosen
   * action code change (the inputs to noteRequired). emitEvent:false so it doesn't
   * trip the userEdited prefill guard.
   */
  private recomputeNoteValidators(): void {
    const allowEmpty = !this.editNoteVal.noteRequired
    this.globalNoteControl.setValidators([
      NotesValidator(allowEmpty),
      NoteContentValidator(),
    ])
    this.globalNoteControl.updateValueAndValidity({ emitEvent: false })
  }

  /**
   * Just close the modal and reset the form
   */
  onCancel() {
    this.activeModal.dismiss()
  }
}
